import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Icon from '../../components/AppIcon';
import { supabase } from '../../lib/supabase';

const PasswordResetForm: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [formData, setFormData] = useState({
    newPassword: '',
    confirmPassword: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState<{
    score: number;
    feedback: string;
    color: string;
  }>({ score: 0, feedback: 'Enter a password', color: 'text-gray-400' });
  const [isValidSession, setIsValidSession] = useState<boolean | null>(null);
  const [sessionChecking, setSessionChecking] = useState(true);

  // Check for valid PASSWORD_RECOVERY session
  useEffect(() => {
    let mounted = true;

    const checkRecoverySession = async () => {
      try {
        // 🔥 ENHANCED: Check URL hash/search for type=recovery indicator
        // This handles cases where Supabase puts tokens in URL before session is ready
        const hashParams = new URLSearchParams(location.hash.substring(1));
        const searchParams = new URLSearchParams(location.search);
        
        const typeInHash = hashParams.get('type');
        const typeInSearch = searchParams.get('type');
        
        const isRecoveryInUrl = typeInHash === 'recovery' || typeInSearch === 'recovery';
        
        if (isRecoveryInUrl) {
          console.log('Recovery indicator found in URL - treating as valid recovery session');
          if (mounted) {
            setIsValidSession(true);
            setSessionChecking(false);
          }
          return;
        }

        const { data: { session }, error } = await supabase.auth.getSession();
        
        if (!mounted) return;

        if (error || !session) {
          console.log('No active session found');
          setIsValidSession(false);
          setSessionChecking(false);
          return;
        }

        // Subscribe to auth state changes to catch PASSWORD_RECOVERY event
        const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
          console.log('Auth event on reset page:', event);
          
          if (!mounted) return;

          if (event === 'PASSWORD_RECOVERY') {
            console.log('PASSWORD_RECOVERY event detected');
            setIsValidSession(true);
            setSessionChecking(false);
          } else if (event === 'SIGNED_IN') {
            setIsValidSession(true);
            setSessionChecking(false);
          } else if (event === 'SIGNED_OUT') {
            setIsValidSession(false);
            setSessionChecking(false);
          }
        });

        // If we already have a session, consider it valid
        if (session) {
          setIsValidSession(true);
          setSessionChecking(false);
        }

        return () => {
          subscription?.unsubscribe();
        };
      } catch (err) {
        console.error('Session check error:', err);
        if (mounted) {
          setIsValidSession(false);
          setSessionChecking(false);
        }
      }
    };

    checkRecoverySession();

    return () => {
      mounted = false;
    };
  }, [location]);

  // Calculate password strength
  useEffect(() => {
    if (!formData.newPassword) {
      setPasswordStrength({ score: 0, feedback: 'Enter a password', color: 'text-gray-400' });
      return;
    }

    let score = 0;
    let feedback = '';
    let color = '';

    // Check length
    if (formData.newPassword.length >= 8) score++;
    if (formData.newPassword.length >= 12) score++;

    // Check for character types
    if (/[a-z]/.test(formData.newPassword) && /[A-Z]/.test(formData.newPassword)) score++;
    if (/\d/.test(formData.newPassword)) score++;
    if (/[^a-zA-Z0-9]/.test(formData.newPassword)) score++;

    // Generate feedback
    if (score <= 2) {
      feedback = 'Weak password';
      color = 'text-red-600';
    } else if (score === 3) {
      feedback = 'Fair password';
      color = 'text-amber-600';
    } else if (score === 4) {
      feedback = 'Good password';
      color = 'text-yellow-600';
    } else {
      feedback = 'Strong password';
      color = 'text-green-600';
    }

    setPasswordStrength({ score, feedback, color });
  }, [formData.newPassword]);

  const validatePassword = (): boolean => {
    // Minimum length check
    if (formData.newPassword.length < 8) {
      setError('Password must be at least 8 characters long');
      return false;
    }

    // Password match check
    if (formData.newPassword !== formData.confirmPassword) {
      setError('Passwords do not match');
      return false;
    }

    // Password strength check
    if (passwordStrength.score < 3) {
      setError('Password is too weak. Please use a mix of uppercase, lowercase, numbers, and special characters');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!validatePassword()) {
      return;
    }

    setIsLoading(true);

    try {
      const { error: updateError } = await supabase.auth.updateUser({
        password: formData.newPassword
      });

      if (updateError) {
        setError(updateError.message);
        setIsLoading(false);
        return;
      }

      setSuccess(true);
      
      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate('/auth/login', { 
          state: { 
            message: 'Password reset successful! You can now sign in with your new password.' 
          } 
        });
      }, 3000);

    } catch (err) {
      console.error('Password reset error:', err);
      setError('An unexpected error occurred. Please try again.');
      setIsLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  // Show loading while checking session
  if (sessionChecking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 py-12 px-4 sm:px-6 lg:px-8">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Verifying reset link...</p>
        </div>
      </div>
    );
  }

  // Show invalid link message
  if (isValidSession === false) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div className="text-center">
            <div className="mx-auto w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
              <Icon name="AlertCircle" size={32} className="text-red-600" />
            </div>
            <h1 className="text-3xl font-bold text-foreground">Invalid Reset Link</h1>
            <p className="text-muted-foreground mt-4">
              This reset link is invalid or has expired. Please request a new password reset.
            </p>
          </div>

          <Button
            onClick={() => navigate('/auth/login')}
            className="w-full"
            iconName="ArrowLeft"
            iconPosition="left"
          >
            Back to Login
          </Button>
        </div>
      </div>
    );
  }

  // Show success state
  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div className="text-center">
            <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
              <Icon name="CheckCircle" size={32} className="text-green-600" />
            </div>
            <h1 className="text-3xl font-bold text-foreground">Password Reset Successful!</h1>
            <p className="text-muted-foreground mt-4">
              Your password has been updated successfully. You can now sign in with your new password.
            </p>
            <p className="text-sm text-muted-foreground mt-2">
              Redirecting to login page in 3 seconds...
            </p>
          </div>

          <Button
            onClick={() => navigate('/auth/login')}
            className="w-full"
            iconName="LogIn"
            iconPosition="left"
          >
            Go to Login Now
          </Button>
        </div>
      </div>
    );
  }

  // Show password reset form
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <div className="mx-auto w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mb-4">
            <Icon name="KeyRound" size={32} className="text-primary" />
          </div>
          <h1 className="text-3xl font-bold text-foreground">Reset Your Password</h1>
          <p className="text-muted-foreground mt-2">
            Enter your new password below
          </p>
        </div>

        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg flex items-start gap-3">
              <Icon name="AlertCircle" size={20} className="text-red-500 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-medium">Error</p>
                <p className="text-xs mt-1">{error}</p>
              </div>
              <button
                type="button"
                onClick={() => setError(null)}
                className="text-red-400 hover:text-red-600"
              >
                <Icon name="X" size={16} />
              </button>
            </div>
          )}

          <div>
            <label htmlFor="newPassword" className="block text-sm font-medium text-foreground mb-2">
              New Password
            </label>
            <div className="relative">
              <Input
                id="newPassword"
                name="newPassword"
                type={showPassword ? "text" : "password"}
                required
                value={formData.newPassword}
                onChange={handleChange}
                placeholder="Enter your new password"
                disabled={isLoading}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground"
                disabled={isLoading}
              >
                <Icon name={showPassword ? "EyeOff" : "Eye"} size={20} />
              </button>
            </div>
            {formData.newPassword && (
              <div className="mt-2">
                <div className="flex items-center gap-2">
                  <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div 
                      className={`h-full transition-all ${
                        passwordStrength.score <= 2 ? 'bg-red-500' : 
                        passwordStrength.score === 3 ? 'bg-amber-500' : 
                        passwordStrength.score === 4 ? 'bg-yellow-500': 'bg-green-500'
                      }`}
                      style={{ width: `${(passwordStrength.score / 5) * 100}%` }}
                    />
                  </div>
                  <span className={`text-xs font-medium ${passwordStrength.color}`}>
                    {passwordStrength.feedback}
                  </span>
                </div>
              </div>
            )}
          </div>

          <div>
            <label htmlFor="confirmPassword" className="block text-sm font-medium text-foreground mb-2">
              Confirm Password
            </label>
            <div className="relative">
              <Input
                id="confirmPassword"
                name="confirmPassword"
                type={showConfirmPassword ? "text" : "password"}
                required
                value={formData.confirmPassword}
                onChange={handleChange}
                placeholder="Confirm your new password"
                disabled={isLoading}
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground"
                disabled={isLoading}
              >
                <Icon name={showConfirmPassword ? "EyeOff" : "Eye"} size={20} />
              </button>
            </div>
            {formData.confirmPassword && formData.newPassword !== formData.confirmPassword && (
              <p className="text-xs text-red-600 mt-1">Passwords do not match</p>
            )}
          </div>

          {/* Password Requirements */}
          <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <h3 className="text-sm font-semibold text-blue-900 mb-2">Password Requirements:</h3>
            <ul className="space-y-1 text-xs text-blue-800">
              <li className="flex items-center gap-2">
                <Icon 
                  name={formData.newPassword.length >= 8 ? "CheckCircle2" : "Circle"} 
                  size={14} 
                  className={formData.newPassword.length >= 8 ? "text-green-600" : "text-gray-400"}
                />
                At least 8 characters long
              </li>
              <li className="flex items-center gap-2">
                <Icon 
                  name={/[a-z]/.test(formData.newPassword) && /[A-Z]/.test(formData.newPassword) ? "CheckCircle2" : "Circle"} 
                  size={14} 
                  className={/[a-z]/.test(formData.newPassword) && /[A-Z]/.test(formData.newPassword) ? "text-green-600" : "text-gray-400"}
                />
                Mix of uppercase and lowercase letters
              </li>
              <li className="flex items-center gap-2">
                <Icon 
                  name={/\d/.test(formData.newPassword) ? "CheckCircle2" : "Circle"} 
                  size={14} 
                  className={/\d/.test(formData.newPassword) ? "text-green-600" : "text-gray-400"}
                />
                At least one number
              </li>
              <li className="flex items-center gap-2">
                <Icon 
                  name={/[^a-zA-Z0-9]/.test(formData.newPassword) ? "CheckCircle2" : "Circle"} 
                  size={14} 
                  className={/[^a-zA-Z0-9]/.test(formData.newPassword) ? "text-green-600" : "text-gray-400"}
                />
                At least one special character
              </li>
            </ul>
          </div>

          <Button
            type="submit"
            className="w-full"
            disabled={isLoading || formData.newPassword !== formData.confirmPassword}
            iconName={isLoading ? "Loader2" : "Lock"}
            iconPosition="left"
          >
            {isLoading ? 'Updating Password...' : 'Reset Password'}
          </Button>
        </form>

        <div className="mt-8 text-center">
          <p className="text-sm text-muted-foreground">
            Remember your password?{' '}
            <button 
              onClick={() => navigate('/auth/login')}
              className="text-primary hover:text-primary/80 font-medium"
            >
              Back to Login
            </button>
          </p>
        </div>
      </div>
    </div>
  );
};

export default PasswordResetForm;